import { motion, useReducedMotion } from "framer-motion";
import { FadeUp } from "./Reveal";

const Arrow = ({ vertical = false }) => (
  <span aria-hidden="true" className={`bos-visual-arrow ${vertical ? "bos-visual-arrow--vertical" : ""}`} />
);

export const BOSSignalLoop = () => {
  const reduceMotion = useReducedMotion();
  const stages = ["Problem appears", "Management intervenes", "Immediate issue resolved", "Problem returns"];
  return (
    <div className="bos-signal" role="img" aria-label="A recurring execution problem moves from problem appears, to management intervenes, to immediate issue resolved, then returns because the underlying condition is unchanged.">
      <div className="bos-signal__track" aria-hidden="true">
        <motion.svg className="bos-signal__path" viewBox="0 0 600 360" preserveAspectRatio="none">
          <motion.path
            d="M132 62 H450 Q526 62 526 138 V222 Q526 298 450 298 H150 Q74 298 74 222 V138 Q74 62 132 62"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            pathLength="1"
            initial={reduceMotion ? false : { pathLength: 0, opacity: 0.15 }}
            whileInView={{ pathLength: 1, opacity: 0.42 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={reduceMotion ? { duration: 0 } : { duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          />
          <path className="bos-signal__return" d="M205 298 H150 Q74 298 74 222" fill="none" stroke="currentColor" strokeWidth="2" />
          <path className="bos-signal__arrowhead" d="M67 231 L74 222 L81 231" fill="none" stroke="currentColor" strokeWidth="2" />
        </motion.svg>
        {stages.map((stage, index) => (
          <FadeUp key={stage} delay={index * 0.06} className={`bos-signal__stage bos-signal__stage--${index + 1}`}>
            <span className="bos-signal__index">{index + 1}</span>
            <span>{stage}</span>
          </FadeUp>
        ))}
        <div className="bos-signal__interrupt"><span>Why it returns</span><strong>Underlying condition unchanged</strong></div>
      </div>
      <div className="bos-signal__examples" aria-label="Examples of recurring execution signals">
        {['escalation', 'checking', 'rework', 'clarification', 'correction'].map((item) => <span key={item}>{item}</span>)}
      </div>
    </div>
  );
};

export const ClarityTestTeaser = () => (
  <div className="clarity-teaser" role="img" aria-label="Clarity that is clear in principle must be tested in real work through pressure, exceptions, competing priorities and judgement.">
    <div className="clarity-teaser__flow"><strong>Clear in principle</strong><Arrow /><strong>Tested in the real work</strong></div>
    <p>pressure <span>·</span> exceptions <span>·</span> competing priorities <span>·</span> judgement</p>
  </div>
);

export const ClarityTestVisual = () => (
  <div className="clarity-test" role="img" aria-label="Stated clarity passes through the pressure of real work to become usable clarity.">
    <div className="clarity-test__side">
      <p className="bos-visual-kicker">Stated clarity</p>
      <strong>Clear in principle</strong>
      {['Communicated', 'Documented', 'Agreed', 'Intended'].map((item) => <span key={item}>{item}</span>)}
    </div>
    <div className="clarity-test__pressure">
      <p className="bos-visual-kicker">Real work</p>
      <strong>The clarity test</strong>
      <div>{['Pressure', 'Exceptions', 'Competing priorities', 'Judgement', 'Imperfect outcomes'].map((item) => <span key={item}>{item}</span>)}</div>
    </div>
    <div className="clarity-test__side clarity-test__side--outcome">
      <p className="bos-visual-kicker">Usable clarity</p>
      <strong>Can people still tell:</strong>
      {['What matters', 'What good looks like', 'What they own', 'What they can decide', 'How to act'].map((item) => <span key={item}>{item}</span>)}
    </div>
  </div>
);

const FIVE_CONDITIONS = ["Direction", "Standards", "Ownership", "Authority", "Behaviour"];

export const FiveConditionsRow = ({ compact = false }) => (
  <div className={`five-conditions ${compact ? "five-conditions--compact" : ""}`}>
    {FIVE_CONDITIONS.map((condition) => <span key={condition}>{condition}</span>)}
  </div>
);

export const LeadershipExecutionVisual = () => {
  const stages = [
    { eyebrow: "What leaders control", title: "The conditions", items: FIVE_CONDITIONS },
    { eyebrow: "What people can rely on", title: "Operational Clarity" },
    { eyebrow: "How people act", title: "Decide · Judge · Own", note: "Escalate appropriately" },
    { eyebrow: "How work gets done", title: "More reliable execution" },
  ];
  return (
    <div className="leadership-execution" role="img" aria-label="What leaders control shapes Operational Clarity, which shapes how people decide, judge, own and escalate, leading to more reliable execution.">
      {stages.map((stage, index) => (
        <div key={stage.eyebrow} className={`leadership-execution__step leadership-execution__step--${index + 1} ${index === 1 || index === 3 ? "leadership-execution__step--focus" : ""}`}>
          <div>
            <p className="bos-visual-kicker">{stage.eyebrow}</p>
            <strong>{stage.title}</strong>
            {stage.items && <p>{stage.items.join(" · ")}</p>}
            {stage.note && <p>{stage.note}</p>}
          </div>
          {index < stages.length - 1 && <Arrow />}
        </div>
      ))}
    </div>
  );
};

export const RevealJourneyVisual = ({ compact = false, dark = false }) => {
  const stages = [
    ["Define", "What keeps happening?"],
    ["Investigate", "What needs closer examination?"],
    ["Reveal", "What does the evidence make visible?"],
    ["Correct", "What should leadership change?"],
    ["Commit", "What will leadership now do differently?"],
  ];
  return (
    <div className={`reveal-journey ${compact ? "reveal-journey--compact" : ""} ${dark ? "reveal-journey--dark" : ""}`} role="img" aria-label="BOS Reveal defines the recurring problem, investigates what needs examination, reveals what the evidence makes visible, corrects what matters, and commits leadership to practical action.">
      {stages.map(([name, question], index) => (
        <div key={name} className={`reveal-journey__stage reveal-journey__stage--${index + 1} ${name === "Reveal" ? "reveal-journey__stage--reveal" : ""}`}>
          <span className="reveal-journey__number">{index + 1}</span>
          <strong>{name}</strong>
          {!compact && <p>{question}</p>}
          {index < stages.length - 1 && <Arrow />}
        </div>
      ))}
    </div>
  );
};
